// Import Packages
import {useRecoilState, useSetRecoilState} from 'recoil';
import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";

// Import Services
import appService from "../../../../services/ApplicationService/ApplicationService";

// Import Material UI
import TextField from "@material-ui/core/TextField";
import {Button} from '@material-ui/core';
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";

// Import Pages
import {alertState, oauthState} from "../../../../recoil-store/recoil-state";

// Import OAuth File
import {oauthGenerator} from "../../../../helpers/oauth/oauth"

// Import External Package
import jwt_decode from "jwt-decode";

// Import Css
import "./AppCredentialProcess.css"

const AppCredential = ({
                           selectedApp,
                           setSelectedApp,
                           appCredentialId,
                           setApplication,
                           cancel,
                           showCancelButton,
                           setRefreshCredentials,
                           setShowCreateConnector,
                           inputColor
                       }) => {
    const [name, setName] = useState('');
    const [form, setForm] = useState({});
    const [authorizationConf, setAuthorizationConf] = useState({});
    const [createStatus, setCreateStatus] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(false);
    const [loading, setLoading] = useState(false);
    const [oauthEnabled, setOauthEnabled] = useState(false);
    const [oauthPopupWindow, setOauthPopupWindow] = useState(null);
    const [oauthPopupState, setOauthPopupState] = useRecoilState(oauthState);
    const [oauthStatus, setOauthStatus] = useState(false);
    const [oauthDisconnect, setOauthDisconnect] = useState("manual");

    const setAlert = useSetRecoilState(alertState);
    const label = "CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.CONNECTORS.CREATE."

    useEffect(() => {
        if (oauthPopupState) {
            oauthPopupWindow.close()
            setOauthPopupState(false);
        }
    }, [oauthPopupState])

    useEffect(() => {
        let f = {};

        if (appCredentialId) {
            const fetchData = async () => {
                const response = await appService.getAppCredential(appCredentialId);
                if (response && response.code === 200) {
                    setName(response.data.name);
                    selectedApp.connector_fields.forEach(field => {
                        if (field.identifier === "authorization") {
                            setAuthorizationConf(field);
                        }
                        if (field.identifier === "authentication_type" && response.data.credentials[field.identifier].value === "oauth20") {
                            setOauthEnabled(true);
                            if (response.data.credentials["access_token"]) {
                                setOauthStatus(true);
                            }

                            if (response.data.credentials.authorization.value.disconnect) {
                                setOauthDisconnect(response.data.credentials.authorization.value.disconnect);
                            }
                        }
                        if (response.data.credentials[field.identifier]) {
                            f[field.identifier] = response.data.credentials[field.identifier];
                        }
                    })
                    setForm(f);
                    setLoading(true);
                }
            }
            fetchData().then(r => r);
        } else {
            // eslint-disable-next-line array-callback-return
            selectedApp.connector_fields.map(field => {
                if (field.identifier === "path") {
                    f[field.identifier] = {type: field.type, value: field.default_value};
                }

                if (field.identifier === "authorization") {
                    setAuthorizationConf(field);
                    f[field.identifier] = {type: field.type, value: {}}
                }

                if (field.visible) {
                    f[field.identifier] = {type: field.type, value: field.default_value}
                }
            })

            setForm(f);
            setLoading(true);
        }
    }, [])

    const {t} = useTranslation();

    const setStringField = (key, value, type) => {
        let copyForm = {...form};
        copyForm[key] = {value, type};
        setForm(copyForm);
    }

    const handleCredentialProcess = async (process) => {
        if (checkInvalidForm()) {
            const data = {
                name: name,
                application_id: selectedApp.id,
                credentials: {...form}
            }

            let response = {};

            switch (process) {
                default:
                    break;
                case "test":
                    response = await appService.testAppCredential(data);
                    if (response && response.status) {
                        appCredentialId ? setUpdateStatus(true) : setCreateStatus(true);
                    }
                    break;
                case "update":
                    response = await appService.updateAppCredential(appCredentialId, data);
                    break;
                case "create":
                    response = await appService.createAppCredential(data);
                    break;
                case "oauth":
                    const refreshToken = localStorage.getItem("refreshToken")
                    const decoded = jwt_decode(refreshToken);

                    const payload = {
                        credential_name: name,
                        application: selectedApp.name,
                        credential_id: appCredentialId,
                        session_id: decoded.session_id,
                        client_id: decoded.id,
                        x_token: JSON.parse(localStorage.getItem("authToken")),
                        credentials: {...form}
                    }
                    const uri = await oauthGenerator(payload);
                    openOauth(uri);
                    break;
                case "oauth-revoke":
                    break;
                case "oauth-revoke-manual":

                    break;
            }

            if (response && response.message) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.TRIGGER-STATIC.CONNECTOR." + response.message),
                    severity: response.status ? 'success' : 'error'
                }));
                if (process !== "test" && response.status) {
                    setRefreshCredentials(new Date());
                    setShowCreateConnector(false);
                    setForm({});
                }
            }

        }
    }

    const checkInvalidForm = () => {
        if (name.length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.APP-CREDENTIAL.NAME-MUST-BE-AT-LEAST"),
                severity: 'info'
            }));
            return false;
        }
        let display_name = '';
        let isInvalid = true;
        for (const field of selectedApp.connector_fields) {
            if (!field.visible) {
                if (field.depends !== '') {
                    const arrName = field.depends.split('.');
                    if (!form[arrName[0]] || !form[arrName[0]].value) {
                        isInvalid = false;
                        display_name = field.display_name
                        break;
                    } else if (form[arrName[0]].value === arrName[1]) {
                        if (!checkValue(form[field.identifier])) {
                            isInvalid = false;
                            display_name = field.display_name
                            break;
                        }
                    }
                }
            } else {
                if (!checkValue(form[field.identifier])) {
                    isInvalid = false;
                    display_name = field.display_name
                    break
                }
            }
        }
        if (!isInvalid) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: `${t(label + display_name)} : ${t("MESSAGES.FIELD-REQUIRED")}`,
                severity: 'info'
            }));
        }
        return isInvalid;
    }

    const checkValue = (data) => {
        if (!data || !data.value) {
            return false;
        }
        let isValid = true;
        switch (data.type) {
            case "string":
            case "password":
            case "email":
                isValid = data.value !== ""
                break;

            default:
                break;
        }

        return isValid;
    }

    const checkVisible = (field) => {
        if (!field.visible) {
            if (field.depends !== '') {
                const arrName = field.depends.split('.');

                return form[arrName[0]] && form[arrName[0]].value === arrName[1]
            }
        }
        return field.visible;
    }

    const handleAuthorizationConfiguration = (f, value) => {
        if (value === "oauth20") {
            setOauthEnabled(true);
        } else {
            setOauthEnabled(false);
        }
        if (authorizationConf.auth_conf) {
            authorizationConf.auth_conf.length && authorizationConf.auth_conf.forEach(conf => {
                const type = conf.depends.split('.')[1];

                if (type === value) {
                    f[authorizationConf.identifier].value = {
                        value_depends: conf.value_depends,
                        disconnect: conf.disconnect,
                        success_response_check: conf.success_response_check,
                        authorization_key: conf.authorization_key,
                        authorization_layer: conf.authorization_layer,
                        authorization_prefix: conf.authorization_prefix,
                        method: conf.method,
                        generate_value: conf.generate_value,
                        additional_layer: conf.additional_layer
                    }
                }
            });

            return f;
        }
    }

    const changeField = (key, value) => {
        let f = {...form};
        f[key].value = value;
        if (key === "authentication_type") {
            f = handleAuthorizationConfiguration(f, value);
        }
        setForm(f);
    }

    const openOauth = (url) => {
        const width = 700;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2.5;
        const title = `${name} OAuth`;
        const popup = window.open(url, title, `width=${width},height=${height},left=${left},top=${top}`);
        setOauthPopupWindow(popup);
    }


    return (
        <div style={{textAlign: "left", width: '100%'}}>
            {
                loading && selectedApp && selectedApp.connector_fields && selectedApp.connector_fields.length && (
                    <>
                        <div className="form-element credential-element">
                            <div className="modal-text add-autom">
                                <div>{t(label + "CONNECTOR-NAME")}</div>
                            </div>
                            <TextField
                                id="filled-size-small"
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                variant="outlined"
                                size="small"
                                inputProps={{
                                    style: {
                                        fontSize: 15,
                                        height: 20,
                                        paddingLeft: 15,
                                        backgroundColor: inputColor ? inputColor : ''
                                    }
                                }}
                            />

                            <small className="help-text">{t(label + 'CONNECTOR-NAME-HELP')}</small>

                        </div>
                        {/* eslint-disable-next-line array-callback-return */}
                        {selectedApp?.connector_fields?.map((field, i) => {
                            const key = field.identifier;
                            if (checkVisible(field)) {
                                switch (field.type) {
                                    case 'string':
                                    case 'password':
                                    case 'email':
                                        return (
                                            <div key={i} className="form-element credential-element">
                                                <div className="modal-text add-autom">
                                                    <div>{t(label + field.display_name)}</div>
                                                </div>
                                                <TextField
                                                    id="filled-size-small"
                                                    type={field.type}
                                                    value={form[key]?.value}
                                                    onChange={(e) => setStringField(key, e.target.value, field.type)}
                                                    variant="outlined"
                                                    size="small"
                                                    inputProps={{
                                                        style: {
                                                            fontSize: 15,
                                                            height: 20,
                                                            paddingLeft: 15,
                                                            backgroundColor: inputColor ? inputColor : ''
                                                        }
                                                    }}
                                                />
                                                {field.help_available && (
                                                    <small
                                                        className="help-text">{t(label + field.display_name + '-HELP')}</small>
                                                )}
                                            </div>
                                        )
                                    case 'single_select':
                                        return <div key={i} className="form-element credential-element">
                                            <div className="modal-text"
                                                 style={{marginBottom: 5}}>{t(label + field.display_name)}</div>
                                            <FormControl
                                                variant="outlined"
                                                style={{width: "100%"}}
                                            >
                                                <Select
                                                    labelId="demo-simple-select-outlined-label"
                                                    style={{
                                                        backgroundColor: inputColor ? inputColor : "#fff",
                                                        height: "41px",
                                                        fontSize: "15px",
                                                        paddingLeft: 15
                                                    }}
                                                    id="demo-simple-select-outlined"
                                                    value={form[key]?.value}
                                                    onChange={(e) => changeField(key, e.target.value)}
                                                >
                                                    {field.options.length > 0 &&
                                                    field.options?.map((item, index) => (
                                                        <MenuItem
                                                            style={{fontSize: 12}}
                                                            key={index}
                                                            value={item.value}
                                                        >
                                                            {item.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {field.help_available && (
                                                    <small
                                                        className="help-text">{t(label + field.display_name + '-HELP')}</small>
                                                )}
                                            </FormControl>
                                        </div>
                                    default:
                                        return (<div/>)
                                }
                            }
                        })}
                    </>
                )
            }
            {oauthEnabled ? (
                <div className="button-group">
                    {showCancelButton && (
                        <Button type="button" onClick={(() => cancel())} className="action-button cancel">
                            {t('CONTENT.USER.CANCEL')}
                        </Button>
                    )}
                    {oauthStatus ? (
                        <Button type="button" className={`action-button oauth-button disconnect test ${!showCancelButton ? "w-48p" : ""}`}
                                onClick={() => handleCredentialProcess(oauthDisconnect === "manual" ? "oauth-revoke-manual" : "oauth-revoke")}>
                            {/* {dialog.type === 'update' ? t('CONTENT.ROLE.UPDATE-ROLE') : t('CONTENT.ROLE.ADD-ROLE')} */}
                            <img className="oauth-img" src={selectedApp.image_path}  alt={selectedApp.name}/>
                            {t(label + 'OAUTH-DISCONNECT')}
                        </Button>

                    ) : (
                        <Button type="button" className={`action-button oauth-button test ${!showCancelButton ? "w-48p" : ""}`}
                                onClick={() => handleCredentialProcess("oauth")}>
                            {/* {dialog.type === 'update' ? t('CONTENT.ROLE.UPDATE-ROLE') : t('CONTENT.ROLE.ADD-ROLE')} */}
                            <img className="oauth-img" src={selectedApp.image_path}  alt={selectedApp.name}/>
                            {t(label + 'OAUTH-CONNECT')}
                        </Button>
                        )}

                </div>
            ) : (
                <div className="button-group">
                    {showCancelButton && (
                        <Button type="button" onClick={(() => cancel())} className="action-button cancel">
                            {t('CONTENT.USER.CANCEL')}
                        </Button>
                    )}

                    <Button type="button" className={`action-button test ${!showCancelButton ? "w-48p" : ""}`}
                            onClick={() => handleCredentialProcess("test")}>
                        {/* {dialog.type === 'update' ? t('CONTENT.ROLE.UPDATE-ROLE') : t('CONTENT.ROLE.ADD-ROLE')} */}
                        {t(label + 'CONNECT')}
                    </Button>
                    <Button type="button"
                            className={`action-button create ${!showCancelButton ? "w-48p" : ""} ${!updateStatus && !createStatus ? "disabled" : ""}`}
                            onClick={appCredentialId ? () => updateStatus && handleCredentialProcess("update") : () => createStatus && handleCredentialProcess("create")}>
                        {/* {dialog.type === 'update' ? t('CONTENT.ROLE.UPDATE-ROLE') : t('CONTENT.ROLE.ADD-ROLE')} */}
                        {appCredentialId ? t(label + 'CONNECT-AND-UPDATE') : t(label + 'CONNECT-AND-CREATE')}
                    </Button>
                </div>
            )}
        </div>
    )

}

export default AppCredential;
